import type { OpportunitySignals } from '../services/signalsService'
import { computeEntryRangeInfo } from './entryRange'

export type TargetPercents = {
  tp1Pct: number | null
  tp2Pct: number | null
  tp3Pct: number | null
  stopBasePct: number | null
  stopAggPct: number | null
  stopConsPct: number | null
}

const EMPTY_PERCENTS: TargetPercents = {
  tp1Pct: null,
  tp2Pct: null,
  tp3Pct: null,
  stopBasePct: null,
  stopAggPct: null,
  stopConsPct: null,
}

export function computeTargetPercents(signals: OpportunitySignals | null): TargetPercents {
  if (!signals) return EMPTY_PERCENTS

  const { entryMid } = computeEntryRangeInfo(signals)
  if (entryMid == null || !Number.isFinite(entryMid)) {
    return EMPTY_PERCENTS
  }

  const fmtPct = (value: number | null): number | null => {
    if (value == null) return null
    const pct = Math.abs(((value - entryMid) / entryMid) * 100)
    if (!Number.isFinite(pct)) return null
    return pct
  }

  const stopBase =
    signals.stopPrice ?? signals.stopAggressive ?? signals.stopConservative ?? null

  return {
    tp1Pct: fmtPct(signals.tp1),
    tp2Pct: fmtPct(signals.tp2),
    tp3Pct: fmtPct(signals.tp3),
    stopBasePct: fmtPct(stopBase),
    stopAggPct: fmtPct(signals.stopAggressive),
    stopConsPct: fmtPct(signals.stopConservative),
  }
}

export type OpportunityPnlInfo = {
  label: string | null
  value: number | null
  variant: 'positive' | 'negative' | 'neutral'
  source: 'tp1' | 'tp2' | 'tp3' | 'stop' | 'none'
}

const EMPTY_PNL: OpportunityPnlInfo = {
  label: null,
  value: null,
  variant: 'neutral',
  source: 'none',
}

export function computeOpportunityPnl(signals: OpportunitySignals | null): OpportunityPnlInfo {
  if (!signals) return EMPTY_PNL

  const { tp1Hit, tp2Hit, tp3Hit, stopHit } = signals
  const percents = computeTargetPercents(signals)

  const chooseLabel = (
    value: number | null,
    source: OpportunityPnlInfo['source'],
    positive: boolean,
  ): OpportunityPnlInfo => {
    if (value == null) return EMPTY_PNL
    const normalized = Number.isFinite(value) ? Number(value.toFixed(2)) : null
    if (normalized == null) return EMPTY_PNL
    const sign = positive ? '+' : '-'
    return {
      label: `${sign} ${normalized.toFixed(2)}% PNL`,
      value: positive ? normalized : -normalized,
      variant: positive ? 'positive' : 'negative',
      source,
    }
  }

  if (stopHit && percents.stopBasePct != null) {
    return chooseLabel(percents.stopBasePct, 'stop', false)
  }

  if (tp3Hit && percents.tp3Pct != null) {
    return chooseLabel(percents.tp3Pct, 'tp3', true)
  }

  if (tp2Hit && percents.tp2Pct != null) {
    return chooseLabel(percents.tp2Pct, 'tp2', true)
  }

  if (tp1Hit && percents.tp1Pct != null) {
    return chooseLabel(percents.tp1Pct, 'tp1', true)
  }

  return EMPTY_PNL
}

